import {
  Button,
  CardMedia,
  Grid,
  TextField,
  TableContainer,
  FormControlLabel,
  Checkbox,
  Switch,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Autocomplete,
  MenuItem,
} from '@mui/material';
import React, { useState, useContext, useEffect } from 'react';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import ItemService from 'services/ItemService';
import { ShopContext } from 'Context/ShopContext';
import { CommonContext } from 'Context/CommonContext';
import { CONFIGS, DAYS_OF_WEEK, CATEGORIES_TAG_SUGGESTIONS, ORDER_TYPE_LIST } from 'const';
import { AlertMessagesContext } from 'react-alert-messages';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';
import ShopsHelper from 'helpers/ShopsHelper';

export default function AddCategoryDialog(props) {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { kitchens, getKitchens } = useContext(CommonContext);
  const { handleClose, onSuccess } = props;

  const [categoryName, setCategoryName] = useState('');
  const [kitchenIp, setKitchenIp] = useState(null);
  const [kitchenName, setKitchenName] = useState(null);
  const [categoryDesc, setCategoryDesc] = useState('');
  const [kitchenId, setKitchenId] = useState('');
  const [order, setOrder] = useState(null);
  const [imageData, setImageData] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeRanges, setTimeRanges] = useState([{ from: null, to: null }]);
  const [tags, setTags] = useState([{ key: '', value: '' }]);
  const [isEnableForCaptain, setIsEnableForCaptain] = useState(true);
  const [isEnableKotPrint, setIsEnableKotPrint] = useState(true);
  const [orderTypes, setOrderTypes] = useState({
    DINE_IN: true,
    TAKE_AWAY: true,
    DELIVERY: true,
    ONLINE: true,
  });

  const handleChange = (index, field, value) => {
    const updated = [...tags];
    updated[index][field] = value;
    setTags(updated);
  };

  const handleAddRow = () => {
    setTags([...tags, { key: '', value: '' }]);
  };

  const handleDeleteRow = (index) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const getKeySuggestions = (input) => {
    return CATEGORIES_TAG_SUGGESTIONS.map((s) => s.key).filter((k) => k.toLowerCase().includes(input.toLowerCase()));
  };

  const getValueSuggestions = (key) => {
    const found = CATEGORIES_TAG_SUGGESTIONS.find((s) => s.key === key);
    return found ? found.values : [];
  };

  const handleDayChange = (event, newDays) => {
    setSelectedDays(newDays);
  };

  const handleTimeRangeChange = (index, field, value) => {
    setTimeRanges((prev) => {
      const arr = [...prev];
      arr[index] = { ...arr[index], [field]: value };
      return arr;
    });
  };

  const addRangeRow = () => {
    setTimeRanges((prev) => [...prev, { from: null, to: null }]);
  };

  const removeRangeRow = (index) => {
    setTimeRanges((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadImage = (image, categoryId) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(image);
      reader.onloadend = async () => {
        const data = {
          image: reader.result,
        };
        try {
          await ItemService.uploadCategoryImage(data, categoryId);
          resolve();
        } catch (e) {
          reject(e);
        }
      };
    });
  };

  const tagsObject = (tagsArray) => {
    const obj = {};
    tagsArray.forEach(({ key, value }) => {
      const k = (key ?? '').trim();
      if (!k) return;

      const v = value ?? '';
      if (Object.prototype.hasOwnProperty.call(obj, k)) {
        if (Array.isArray(obj[k])) {
          obj[k].push(v);
        } else {
          obj[k] = [obj[k], v];
        }
      } else {
        obj[k] = v;
      }
    });
    return obj;
  };

  const handleAddCategory = async () => {
    setLoadingIndicator(true);

    const hasAnySchedule = selectedDays.length > 0 || timeRanges.some(({ from, to }) => from != null || to != null);
    const tagsObj = tagsObject(tags);

    let daysStr = null;
    let timeRangesStr = null;

    if (hasAnySchedule) {
      for (const { from, to } of timeRanges) {
        if (!from || !to) {
          postAlertMessage({
            text: tManagement('Fill both start end time'),
            type: 'failed',
          });
          setLoadingIndicator(false);
          return;
        }
        if (from >= to) {
          postAlertMessage({
            text: tManagement('Start time must be earlier than end time'),
            type: 'failed',
          });
          setLoadingIndicator(false);
          return;
        }
      }

      daysStr = selectedDays.length ? selectedDays.join(',') : null;

      const schedulePayload = timeRanges.map(({ from, to }) => {
        const pad = (n) => String(n).padStart(2, '0');
        const fH = pad(from.getHours()),
          fM = pad(from.getMinutes());
        const tH = pad(to.getHours()),
          tM = pad(to.getMinutes());
        return `${fH}:${fM}-${tH}:${tM}`;
      });

      timeRangesStr = schedulePayload.join(',');
    }

    try {
      const data = {
        name: categoryName,
        description: categoryDesc,
        kitchen_ip: kitchenIp,
        kitchen_name: kitchenName,
        kitchen_id: kitchenId ? Number(kitchenId) : null,
        order,
        available_days: daysStr,
        available_time_range: timeRangesStr,
        config: {
          tags: tagsObj,
          kot_settings: {
            is_enable_for_captain: isEnableForCaptain,
            is_enable_kot_print: isEnableKotPrint,
            order_types: orderTypes,
          },
        },
      };

      const res = await ItemService.addItemCategory(data);
      if (data.image && data.image.size < CONFIGS.MAX_IMAGE_SIZE) {
        await uploadImage(imageData, res.id);
      }

      postAlertMessage({ text: tManagement('Category created successfully'), type: 'success' });
      onSuccess(data.image ? null : res);
      handleClose();
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleEnableForCaptain = (value) => {
    setIsEnableForCaptain(value);
  };
  const handleChangeKotPrint = (value) => {
    setIsEnableKotPrint(value);
    if (value) {
      setOrderTypes({
        DINE_IN: true,
        TAKE_AWAY: true,
        DELIVERY: true,
        ONLINE: true,
      });
      return;
    }
    setOrderTypes({
      DINE_IN: false,
      TAKE_AWAY: false,
      DELIVERY: false,
      ONLINE: false,
    });
  };

  const handleOrderTypeChange = (key) => {
    setOrderTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    getKitchens();
  }, [getKitchens]);

  return (
    <div className={styles.popUp} style={{ zIndex: 1400 }}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tCommon('Create New Category')}</h2>
          <CloseIcon onClick={handleClose} />
        </div>
        <div className={styles.inputSec}>
          <Grid container alignItems="top" spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Category Name')}
                variant="outlined"
                size="small"
                fullWidth
                value={categoryName}
                onChange={(e) => {
                  setCategoryName(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Category Description')}
                variant="outlined"
                size="small"
                fullWidth
                value={categoryDesc}
                onChange={(e) => {
                  setCategoryDesc(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField
                label={tCommon('Display Order')}
                value={order}
                onChange={(e) => {
                  setOrder(e.target.value);
                }}
              />
            </Grid>

            {(shop.kot_lite_validity || shop.kot_validity) && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label={tCommon('Select Kitchen') || 'Select Kitchen'}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={kitchenId}
                    onChange={(e) => setKitchenId(e.target.value)}
                    SelectProps={{
                      MenuProps: {
                        getContentAnchorEl: null,
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                        PaperProps: { style: { maxHeight: 350 } },
                        style: { zIndex: 10002 },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{tCommon('None') || 'None'}</em>
                    </MenuItem>
                    {(kitchens || []).map((k) => {
                      const chefName = k.chef?.name || (typeof k.chef === 'string' && k.chef !== '-' ? k.chef : '');
                      return (
                        <MenuItem key={k.id} value={k.id}>
                          {k.name
                            ? chefName
                              ? `${k.name} (Chef: ${chefName})`
                              : k.name
                            : chefName
                            ? `Chef: ${chefName}`
                            : `Kitchen ${k.id}`}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={tCommon('Kitchen Name')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={kitchenName}
                    onChange={(e) => {
                      setKitchenName(e.target.value);
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {(shop.kot_lite_validity || shop.kot_validity) && (
            <Grid container m={1} className={styles.subContainer} style={{ margin: '10px 0' }}>
              <Grid p={1}>
                <h3 className={styles.inputLabel}>{tCommon('KOT Settings')}</h3>
              </Grid>

              <Grid item container xs={12} component={Paper}>
                <Grid
                  item
                  xs={12}
                  style={{ display: 'flex', alignItems: 'center', padding: '10px', justifyContent: 'space-between' }}
                >
                  <span>{tCommon('Enable For Captain')}</span>
                  <Switch
                    checked={isEnableForCaptain}
                    color="primary"
                    onChange={() => {
                      handleEnableForCaptain(!isEnableForCaptain);
                    }}
                    slotProps={{ input: { 'aria-label': 'controlled' } }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  style={{ display: 'flex', alignItems: 'center', padding: '10px', justifyContent: 'space-between' }}
                >
                  <span>{tCommon('Enable KOT Print')}</span>
                  <Switch
                    checked={isEnableKotPrint}
                    color="primary"
                    onChange={() => {
                      handleChangeKotPrint(!isEnableKotPrint);
                    }}
                    slotProps={{ input: { 'aria-label': 'controlled' } }}
                  />
                </Grid>
                {isEnableKotPrint && (
                  <Grid container className={styles.labelContainer} spacing={1} style={{ paddingBottom: '10px' }}>
                    <Grid item xs={12} sm={6}>
                      <span>{tCommon('Kitchen IP Address')}</span>
                    </Grid>
                    <Grid item xs={12} sm={6} style={{ padding: '10px 0' }}>
                      <TextField
                        label={tCommon('Kitchen Ip')}
                        variant="outlined"
                        fullWidth
                        value={kitchenIp}
                        onChange={(e) => {
                          setKitchenIp(e.target.value);
                        }}
                      />
                    </Grid>
                    {ORDER_TYPE_LIST.map(({ key, label, icon }) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Paper
                          m={2}
                          key={key}
                          className={`${styles.option} ${orderTypes[key] ? styles.optionActive : ''}`}
                          elevation={0}
                          component="label"
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="primary"
                                checked={orderTypes[key]}
                                onChange={() => handleOrderTypeChange(key)}
                                className={styles.checkbox}
                              />
                            }
                            label={
                              <div className={styles.labelContent}>
                                <span className={styles.icon}>{icon}</span>
                                <span className={styles.label}>{label}</span>
                              </div>
                            }
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}

          {shop && shop.image_support_validity && (
            <Grid container item mt={2} xs={12}>
              <Grid item xs={6}>
                <Button variant="contained" size="small" className={styles.uploadBtn}>
                  <label for="upload">{imageData ? tCommon('Change Image') : tCommon('Choose Image')}</label>
                </Button>
              </Grid>
              <Grid item xs={6} sm={6}>
                <div style={{ fontSize: 16, paddingBottom: 5 }}>{tCommon('150px * 150px size recommended')}</div>
                {imageData && <CardMedia image={URL.createObjectURL(imageData)} className={styles.categoryImg} />}
                {imageData && imageData.size > CONFIGS.MAX_IMAGE_SIZE && (
                  <div className={styles.imageSizeWarning}>
                    {tCommon('*Image size exceeds maximum limit. Upload file size less than {{kb}}KB', { kb: 100 })}
                  </div>
                )}

                <input
                  accept="image/*"
                  type="file"
                  hidden
                  id="upload"
                  onChange={(e) => {
                    setImageData(e.target.files[0]);
                  }}
                />
              </Grid>
            </Grid>
          )}

          {/* Category tags */}
          {ShopsHelper.hasPremiumAccess(shop) && (
            <Grid
              container
              className={styles.inputRow}
              alignItems="center"
              style={{ marginTop: '10px', paddingLeft: '0' }}
            >
              <Grid container className={styles.subContainer} style={{ marginTop: '32px' }}>
                <Grid p={1}>
                  <h3 className={styles.inputLabel}>{tCommon('Category Tags')}</h3>
                </Grid>
                <Grid item xs={12}>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>{tCommon('Key')}</strong>
                          </TableCell>
                          <TableCell>
                            <strong>{tCommon('Value')}</strong>
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tags.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={getKeySuggestions(row.key)}
                                value={row.key}
                                onChange={(e, newValue) => handleChange(index, 'key', newValue || '')}
                                onInputChange={(e, newValue) => handleChange(index, 'key', newValue)}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder={tCommon('Key')} variant="outlined" />
                                )}
                              />
                            </TableCell>

                            <TableCell>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={getValueSuggestions(row.key)}
                                value={row.value}
                                onChange={(e, newValue) => handleChange(index, 'value', newValue || '')}
                                onInputChange={(e, newValue) => handleChange(index, 'value', newValue)}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder={tCommon('Value')} variant="outlined" />
                                )}
                              />
                            </TableCell>

                            <TableCell align="right">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteRow(index)}
                                disabled={tags.length === 1}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Grid container style={{ paddingTop: '10px' }}>
                              <Grid item xs={12} sm={12} p={1}>
                                <Button
                                  startIcon={<LibraryAddIcon />}
                                  onClick={handleAddRow}
                                  size="small"
                                  variant="outlined"
                                >
                                  {tCommon('Add New Tag')}
                                </Button>
                              </Grid>
                            </Grid>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Grid>
          )}

          {ShopsHelper.hasPremiumAccess(shop) && (
            <Grid container className={styles.inputRow} alignItems="center" style={{ marginTop: '12px', padding: '0' }}>
              <Grid container className={styles.subContainer}>
                <Grid p={1}>
                  <h3 className={styles.inputLabel}>{tCommon('Category Schedule')}</h3>
                </Grid>
                <Grid container className={styles.inputRow} alignItems="center">
                  <Grid item xs={12} sm={2} className={styles.inputLabelContainer}>
                    <h3 className={styles.inputSubLabel}>{tCommon('Available Days')}</h3>
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <ToggleButtonGroup
                      value={selectedDays}
                      onChange={handleDayChange}
                      aria-label="days of the week"
                      size="small"
                      fullWidth
                    >
                      {Object.values(DAYS_OF_WEEK).map((day) => (
                        <ToggleButton
                          key={day}
                          value={day}
                          aria-label={day}
                          style={{
                            padding: '6px',
                            color: selectedDays.includes(day) ? '#fff' : '#333',
                            backgroundColor: selectedDays.includes(day) ? '#0b8300a8' : '#ffffff00',
                            border: '1px solid #ccc',
                            borderRadius: 3,
                            fontWeight: 500,
                          }}
                        >
                          {day}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>

                {timeRanges.map((tr, idx) => (
                  <Grid container className={styles.inputRow} alignItems="center" key={idx}>
                    <Grid item xs={12} sm={2} className={styles.inputLabelContainer}>
                      <h3 className={styles.inputSubLabel}>{idx === 0 ? tCommon('Available Time') : ''}</h3>
                    </Grid>

                    <Grid xs={12} sm={10}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={5}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <TimePicker
                              label={tCommon('Start Time')}
                              value={tr.from}
                              onChange={(newVal) => handleTimeRangeChange(idx, 'from', newVal)}
                              format="hh:mma"
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  InputProps: {
                                    sx: {
                                      height: 40,
                                      fontSize: '0.875rem',
                                      alignItems: 'center',
                                      '& input': { textAlign: 'center' },
                                    },
                                  },
                                  InputLabelProps: {
                                    sx: { width: '100%', textAlign: 'center' },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>

                        <Grid item xs={5}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <TimePicker
                              label={tCommon('End Time')}
                              value={tr.to}
                              onChange={(newVal) => handleTimeRangeChange(idx, 'to', newVal)}
                              format="hh:mma"
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  InputProps: {
                                    sx: {
                                      height: 40,
                                      fontSize: '0.875rem',
                                      alignItems: 'center',
                                      '& input': { textAlign: 'center' },
                                    },
                                  },
                                  InputLabelProps: {
                                    sx: { width: '100%', textAlign: 'center' },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>

                        <Grid item xs={2}>
                          <IconButton
                            size="small"
                            onClick={() => removeRangeRow(idx)}
                            disabled={timeRanges.length === 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}

                <Grid container>
                  <Grid item xs={12} sm={12} p={1}>
                    <Button size="small" variant="outlined" startIcon={<LibraryAddIcon />} onClick={addRangeRow}>
                      {tCommon('Add New Time Schedule')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleClose}
              className={styles.closeBtn}
              disabled={loadingIndicator}
            >
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ backgroundColor: '#00a65a' }}
              disabled={categoryName === '' || loadingIndicator}
              onClick={handleAddCategory}
            >
              {loadingIndicator ? (
                <>
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                  {tCommon('Creating...')}
                </>
              ) : (
                tCommon('Create Category')
              )}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
